import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, XCircle, Clock, Lock, ShieldCheck, FileText, Scale,
  ListChecks, Users, Target, CalendarRange, Search, ClipboardList, ShieldAlert,
  UserCheck, TrendingUp, Timer, Camera, HeartPulse, Award, QrCode, Mail,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function Landing() {
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      <main className="flex-1">
        {/* HERO */}
        <section className="border-b hairline bg-white">
          <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 border border-navy/20 bg-navy/5 text-navy px-3 py-1 rounded-sm">
                <ShieldCheck size={13} />
                <span className="text-[11px] font-mono uppercase tracking-widest">Sistema professionale</span>
              </div>
              <h1 className="font-serif text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.03] mt-5" data-testid="hero-title">
                PERIZIA READY
              </h1>
              <p className="mt-5 font-serif text-2xl lg:text-3xl text-slate-800 leading-snug max-w-2xl">
                Valutazione preliminare della preparazione documentale del fascicolo grafologico.
              </p>
              <p className="mt-6 text-slate-600 text-base leading-relaxed max-w-2xl">
                Uno strumento pensato per avvocati, consulenti tecnici e privati che devono
                organizzare un fascicolo prima di richiedere una perizia, affrontare una CTU o
                depositare una consulenza tecnica di parte.
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/wizard" data-testid="hero-start-btn"
                  className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white px-7 py-3.5 rounded-sm text-sm font-semibold transition-colors">
                  Inizia la valutazione <ArrowRight size={16} />
                </Link>
                <a href="#come-funziona"
                  className="inline-flex items-center gap-2 border hairline hover:border-navy text-slate-700 px-6 py-3.5 rounded-sm text-sm font-medium transition-colors">
                  Come funziona
                </a>
              </div>

              <div className="mt-8 grid grid-cols-3 gap-6 max-w-lg">
                <div className="border-l-2 border-navy pl-3">
                  <div className="flex items-center gap-1 text-slate-500"><Clock size={12} /> <span className="text-[10px] uppercase tracking-widest font-mono">Tempo</span></div>
                  <div className="font-serif text-xl font-semibold mt-1">10-15 min</div>
                </div>
                <div className="border-l-2 border-navy pl-3">
                  <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Percorso</div>
                  <div className="font-serif text-lg font-semibold mt-1 leading-tight">Guidato,<br />personalizzato</div>
                </div>
                <div className="border-l-2 border-navy pl-3">
                  <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Report</div>
                  <div className="font-serif text-xl font-semibold mt-1">Gratuito · Premium</div>
                </div>
              </div>

              <p className="mt-8 text-xs text-slate-500 flex items-start gap-2 max-w-xl">
                <Lock size={12} className="mt-0.5 shrink-0 text-slate-400" />
                <span>I dati inseriti sono trattati in via riservata secondo il GDPR. Le informazioni
                servono unicamente a costruire il tuo fascicolo. Non conserviamo copie del documento contestato.</span>
              </p>
            </div>

            {/* Panel: cosa misura / cosa non misura */}
            <aside className="lg:col-span-5 space-y-4" data-testid="scope-panel">
              <div className="border-2 border-navy bg-navy p-6 text-white">
                <div className="text-[10px] uppercase tracking-widest font-mono text-white/70">Cosa misura Perizia Ready</div>
                <ul className="mt-4 space-y-2.5 text-sm">
                  {[
                    "Disponibilità del documento in verifica (originale, scansione, copia).",
                    "Quantità e qualità delle scritture di comparazione.",
                    "Rappresentatività temporale delle comparative.",
                    "Completezza delle informazioni di contesto.",
                    "Elementi procedurali dichiarati (CTU, verificazione, termini).",
                  ].map((t) => (
                    <li key={t} className="flex gap-2.5">
                      <CheckCircle2 size={16} className="shrink-0 mt-0.5 text-forensic-green" />
                      <span className="text-white/90 leading-snug">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border hairline bg-white p-6">
                <div className="overline text-forensic-red">Cosa NON misura</div>
                <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
                  {[
                    "L'autenticità della firma o della scrittura.",
                    "La probabilità che il documento sia stato falsificato.",
                    "La probabilità di successo processuale.",
                    "Il valore probatorio del documento in giudizio.",
                    "L'attendibilità della tesi dell'utente.",
                  ].map((t) => (
                    <li key={t} className="flex gap-2.5">
                      <XCircle size={16} className="shrink-0 mt-0.5 text-forensic-red/70" />
                      <span className="leading-snug">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </section>

        {/* PACKAGES: gratuito vs premium — "Due livelli di output" */}
        <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20" id="pacchetti">
          <div className="max-w-3xl">
            <div className="overline mb-3">Due livelli di output</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-semibold tracking-tight">
              Scegli quanto approfondire.
            </h2>
            <p className="text-slate-600 mt-4 text-base leading-relaxed">
              Valuta subito il tuo fascicolo con il report gratuito o passa alla versione
              completa per un'analisi professionale senza compromessi.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {/* ---------- GRATUITO ---------- */}
            <div className="border hairline bg-white p-8 relative" data-testid="pkg-free">
              <div className="overline text-forensic-green">Gratuito · sempre disponibile</div>

              <div className="mt-3 flex items-start justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-3xl font-semibold leading-tight">
                    Rapporto sommario preliminare
                  </div>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed max-w-md">
                    Un'analisi immediata per capire a colpo d'occhio lo stato reale
                    del tuo fascicolo e le mosse più intelligenti da fare.
                  </p>
                </div>
                {/* Gauge mock */}
                <div className="shrink-0 w-32 text-center hidden sm:block" aria-hidden="true">
                  <svg viewBox="0 0 100 60" className="w-full">
                    <path d="M 8 55 A 42 42 0 0 1 92 55" fill="none" stroke="#E2E8F0" strokeWidth="8" strokeLinecap="round" />
                    <path d="M 8 55 A 42 42 0 0 1 78 20" fill="none" stroke="#2F855A" strokeWidth="8" strokeLinecap="round" />
                  </svg>
                  <div className="font-mono text-2xl font-bold text-forensic-green -mt-1">72<span className="text-xs text-slate-400">/100</span></div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Indice di preparazione</div>
                  <div className="text-[11px] text-forensic-green font-medium">Buono</div>
                </div>
              </div>

              <ul className="mt-6 space-y-4 border-t hairline pt-6">
                {[
                  [Target, "Chiarezza immediata",
                    "Comprendi subito punti di forza, criticità e livello di lavorabilità del fascicolo."],
                  [CalendarRange, "Timeline unica e integrata",
                    "Le scritture comparative vengono messe in ordine temporale e incrociate con gli eventi rilevanti del caso e con la cartella clinica.",
                    "Vedi la storia completa, non solo i documenti."],
                  [Scale, "Decisioni più consapevoli",
                    "Capisci se il fascicolo è già pronto per una perizia completa o se è il caso di integrare la documentazione."],
                  [ShieldAlert, "Riduzione dei rischi",
                    "Individui subito carenze, incoerenze e vuoti documentali che potrebbero indebolire la tua posizione."],
                  [Timer, "Risparmio di tempo e costi",
                    "Eviti richieste inutili e concentri le risorse dove davvero servono."],
                  [UserCheck, "Confronto facilitato con il consulente",
                    "Arrivi al confronto professionale con una base chiara, condivisa e già organizzata."],
                ].map(([Icon, title, desc, highlight]) => (
                  <li key={title} className="flex gap-3 leading-snug">
                    <div className="w-9 h-9 rounded-full bg-forensic-green/10 border border-forensic-green/30 flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-forensic-green" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-serif text-base font-semibold text-forensic-green">{title}</div>
                      <p className="text-sm text-slate-700 mt-1 leading-relaxed">{desc}</p>
                      {highlight && (
                        <p className="text-sm text-forensic-green font-medium mt-1">{highlight}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-6 bg-forensic-green/5 border border-forensic-green/30 p-4">
                <div className="overline text-forensic-green mb-2">Cosa ricevi subito</div>
                <p className="text-xs text-slate-700 leading-relaxed">
                  Indice di preparazione · Classificazione del fascicolo · Urgenza e lavorabilità · Punti di forza
                  · Criticità principali · Prime azioni consigliate · Timeline essenziale
                </p>
              </div>
            </div>

            {/* ---------- PREMIUM ---------- */}
            <div className="border-2 border-navy bg-navy text-white p-8 relative" data-testid="pkg-premium">
              <div className="overline text-forensic-amber">Premium</div>

              <div className="mt-3">
                <div className="font-serif text-3xl font-semibold leading-tight">
                  Report Premium
                </div>
                <p className="text-sm text-white/85 mt-3 font-medium">
                  Il report completo che trasforma i dati in strategia.
                </p>
                <p className="text-sm text-white/70 mt-2 leading-relaxed max-w-md">
                  Un'analisi professionale e profonda che mette in relazione ogni elemento del caso
                  per offrirti una visione chiara, solida e utilizzabile.
                </p>
                <p className="text-white/60 text-[11px] mt-3">
                  Detraibile in caso di successivo conferimento incarico.
                </p>
              </div>

              {/* Timeline mock */}
              <div className="mt-6 border-t border-white/15 pt-5" aria-hidden="true">
                <div className="relative">
                  <div className="absolute left-3 right-3 top-4 h-px bg-white/25"></div>
                  <div className="grid grid-cols-4 gap-2 relative">
                    {[
                      [Camera, "2018", "Comparativa", "#F59E0B"],
                      [HeartPulse, "2019", "Evento clinico", "#3B82F6"],
                      [Target, "2020", "Ricovero", "#A855F7"],
                      [CheckCircle2, "2021", "Comparativa", "#10B981"],
                    ].map(([Icon, y, l, c]) => (
                      <div key={y} className="text-center">
                        <div className="w-8 h-8 rounded-full mx-auto flex items-center justify-center border-2"
                             style={{ borderColor: c, backgroundColor: "#0f2544" }}>
                          <Icon size={14} style={{ color: c }} />
                        </div>
                        <div className="text-[10px] font-mono text-white/70 mt-2">{y}</div>
                        <div className="text-[10px] text-white/60 leading-tight">{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <ul className="mt-6 space-y-4">
                {[
                  [CalendarRange, "Timeline avanzata e integrata",
                    "Ogni scrittura comparativa è posizionata sulla linea del tempo e incrociata con eventi, stato di salute e terapie.",
                    "Capisci il contesto, non solo la grafia."],
                  [Search, "Analisi completa delle comparative",
                    "Valutazione dettagliata di qualità, quantità, rappresentatività e coerenza temporale.",
                    "Sai quali scritture contano davvero."],
                  [HeartPulse, "Integrazione con la cartella clinica",
                    "Sintesi mirata di patologie, terapie e condizioni rilevanti nel periodo delle scritture.",
                    "Il quadro clinico che serve, quando serve."],
                  [Scale, "Valutazione tecnico-strategica",
                    "Punti di forza, limiti e rischi del fascicolo con indicazioni operative per orientare scelte e strategie.",
                    "Non solo dati: una direzione chiara."],
                  [ListChecks, "Piano \u201cCome migliorare il fascicolo\u201d",
                    "Azioni prioritarie per colmare le lacune e aumentare il valore probatorio.",
                    "Una roadmap concreta, passo dopo passo."],
                  [Target, "Prossimo passo consigliato",
                    "Consulenza orale, parere sintetico o perizia completa: il percorso più adatto al tuo caso.",
                    "Un'indicazione chiara per attivare subito il servizio."],
                ].map(([Icon, title, desc, highlight]) => (
                  <li key={title} className="flex gap-3 leading-snug">
                    <div className="w-9 h-9 rounded-full bg-forensic-amber/10 border border-forensic-amber/40 flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-forensic-amber" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-serif text-base font-semibold text-forensic-amber">{title}</div>
                      <p className="text-sm text-white/80 mt-1 leading-relaxed">{desc}</p>
                      {highlight && (
                        <p className="text-sm text-forensic-amber font-medium mt-1">{highlight}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-6 border border-forensic-amber/40 bg-forensic-amber/10 p-4">
                <div className="flex items-start gap-2">
                  <Award size={16} className="text-forensic-amber shrink-0 mt-0.5" />
                  <div>
                    <div className="overline text-forensic-amber mb-1">Vantaggio competitivo per avvocati e CTP</div>
                    <p className="text-xs text-white/85 leading-relaxed">
                      Più preparazione, più strategia, più credibilità davanti al giudice.
                      Un fascicolo ben costruito oggi vale una perizia solida domani.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom footer bar */}
          <div className="mt-8 grid md:grid-cols-2 gap-6 bg-white border hairline p-6">
            <div className="flex gap-4 items-start">
              <div className="w-11 h-11 rounded-full bg-navy/10 flex items-center justify-center shrink-0">
                <TrendingUp size={18} className="text-navy" />
              </div>
              <div>
                <div className="font-serif text-lg font-semibold">Perché è diverso</div>
                <p className="text-sm text-slate-600 leading-relaxed mt-1">
                  La nostra analisi non si limita a contare le comparative: le mette in ordine,
                  le colloca nel tempo e le confronta con eventi e condizioni cliniche.
                </p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-11 h-11 rounded-full bg-navy/10 flex items-center justify-center shrink-0">
                <Target size={18} className="text-navy" />
              </div>
              <div>
                <div className="font-serif text-lg font-semibold">Risultato</div>
                <p className="text-sm text-slate-600 leading-relaxed mt-1">
                  Una visione completa del caso, non una lista di documenti. Questo fa la differenza
                  tra un fascicolo qualsiasi e un fascicolo che ha valore.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="come-funziona" className="border-y hairline bg-white">
          <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12">
            <div>
              <div className="overline mb-3">Come funziona</div>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold tracking-tight">
                Un percorso guidato, non un parere automatico.
              </h2>
              <p className="text-slate-600 mt-4 leading-relaxed">
                Rispondi a una serie di domande sul documento contestato, sulle scritture di
                comparazione e sulla fase del procedimento. Il sistema costruisce un indice di
                preparazione, un piano documentale e — al termine — un report differenziato
                per il richiedente e per l'avvocato.
              </p>
              <div className="mt-6 flex gap-6 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <Users size={14} className="text-navy" /> Percorso privato o avvocato
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <Scale size={14} className="text-navy" /> Terminologia tecnico-processuale
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <FileText size={14} className="text-navy" /> Report PDF professionale
                </div>
              </div>
            </div>
            <ol className="space-y-6">
              {[
                ["01", "Identificazione del caso", "Tipo di documento, contestazione, disponibilità dell'originale."],
                ["02", "Analisi delle comparative", "Quantità, qualità, provenienza, distribuzione temporale."],
                ["03", "Cronologia e strategia", "Timeline e nodi processuali (verificazione, CTU, termini)."],
                ["04", "Report e piano documentale", "Indice, criticità, azioni prioritarie, servizio consigliato."],
              ].map(([n, t, d]) => (
                <li key={n} className="grid grid-cols-[auto_1fr] gap-6 border-l hairline pl-6">
                  <span className="font-mono text-sm text-navy">{n}</span>
                  <div>
                    <div className="font-serif text-xl font-semibold">{t}</div>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="overline mb-3">Pronto a iniziare?</div>
          <h2 className="font-serif text-4xl lg:text-5xl font-semibold tracking-tighter">
            In pochi minuti hai un fascicolo pronto per il consulente.
          </h2>
          <p className="mt-4 text-slate-600">
            Il salvataggio automatico ti consente di riprendere la compilazione in un secondo momento.
          </p>
          <Link to="/wizard" data-testid="footer-cta-btn"
            className="inline-flex items-center gap-2 mt-8 bg-navy hover:bg-navy-dark text-white px-8 py-4 rounded-sm text-sm font-semibold transition-colors">
            Inizia la valutazione <ArrowRight size={16} />
          </Link>
        </section>

        {/* CONTATTO DIRETTO — informativo, non sostituisce CTA funnel */}
        <section id="contatto" className="border-t hairline bg-white" data-testid="home-contact">
          <div className="max-w-4xl mx-auto px-6 py-14 lg:py-16 text-center">
            <div className="w-12 h-12 rounded-full bg-navy/5 border border-navy/15 flex items-center justify-center mx-auto">
              <Mail size={18} className="text-navy" />
            </div>
            <div className="overline mt-4 mb-2">Contatto diretto</div>
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold tracking-tight" data-testid="home-contact-title">
              Hai bisogno di chiarimenti?
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Non sai se Perizia Ready è adatto al tuo caso? Per informazioni sul funzionamento
              del servizio, sui report o sulla preparazione del fascicolo, puoi contattarci
              direttamente.
            </p>
            <div className="mt-6">
              <a href="mailto:info@periziafirma.it" data-testid="home-contact-email"
                 className="font-mono text-sm text-navy underline-offset-4 hover:underline break-all">
                info@periziafirma.it
              </a>
            </div>
            <a href="mailto:info@periziafirma.it" data-testid="home-contact-cta"
               className="inline-flex items-center gap-2 mt-6 border border-navy text-navy hover:bg-navy hover:text-white px-6 py-3 rounded-sm text-sm font-semibold transition-colors">
              <Mail size={14} /> Scrivi a Perizia Ready
            </a>
            <p className="mt-5 text-xs text-slate-500 leading-relaxed max-w-xl mx-auto">
              Indirizzo informativo generale. Per l'invio del fascicolo al consulente o la
              richiesta di confronto tecnico usa il percorso guidato in piattaforma.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
